import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { authApi } from '@/services/api';

const KEYCLOAK_URL = import.meta.env['VITE_KEYCLOAK_URL'] || 'http://keycloak.localhost';
const KEYCLOAK_REALM = import.meta.env['VITE_KEYCLOAK_REALM'] || 'stcaria';

// Refresh token 2 minutes before expiry
const REFRESH_MARGIN_MS = 2 * 60 * 1000;

export function useAuth() {
  const { login, logout: storeLogout, setLoading, setToken, setUser, token, idToken } = useAuthStore();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initCalledRef = useRef(false);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await authApi.logout();
      }
    } finally {
      storeLogout();
      // Build logout URL with id_token_hint for Keycloak
      const logoutUrl = new URL(`${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`);
      logoutUrl.searchParams.append('post_logout_redirect_uri', window.location.origin + '/login');
      if (idToken) {
        logoutUrl.searchParams.append('id_token_hint', idToken);
      }
      window.location.href = logoutUrl.toString();
    }
  }, [storeLogout, token, idToken]);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const result = await authApi.refresh();
      setToken(result.accessToken, result.expiresIn);
      setUser(result.user);
      return result.accessToken;
    } catch {
      storeLogout();
      return null;
    }
  }, [setToken, setUser, storeLogout]);

  // Initialize auth - validate session on mount (runs once)
  const initAuth = useCallback(async () => {
    if (initCalledRef.current) return;
    initCalledRef.current = true;

    const { isAuthenticated: authed, token: currentToken } = useAuthStore.getState();

    if (!authed) {
      setLoading(false);
      return;
    }

    // If we have no token (page reload), try to restore via refresh
    if (!currentToken) {
      setLoading(true);
      try {
        const result = await authApi.refresh();
        login(result.accessToken, result.user, result.sessionId || useAuthStore.getState().sessionId || '', result.expiresIn, result.idToken || '');
      } catch {
        storeLogout();
      } finally {
        setLoading(false);
      }
      return;
    }

    // Validate current session
    try {
      const user = await authApi.me();
      setUser(user);
    } catch {
      // Token might be expired, try refresh
      await refreshToken();
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Setup proactive token refresh timer
  const setupTokenRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const { expiresAt } = useAuthStore.getState();
    if (!expiresAt) return;

    const timeUntilRefresh = expiresAt - Date.now() - REFRESH_MARGIN_MS;
    if (timeUntilRefresh <= 0) {
      // Already needs refresh
      refreshToken();
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, timeUntilRefresh);
  }, [refreshToken]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Setup refresh timer when token changes
  useEffect(() => {
    if (token) {
      setupTokenRefresh();
    }
  }, [token, setupTokenRefresh]);

  return {
    initAuth,
    logout,
    refreshToken,
    setupTokenRefresh,
  };
}
